// What the session view shows, derived from frames: the turn timeline and
// the side panel's state. Pure functions, so the grouping and pairing are
// tested without a DOM.
import {
  capsOf,
  type Actor,
  type AlertBody,
  type Caps,
  type Envelope,
  type EventBody,
  type InjectOutcome,
  type Participant,
  type Policy,
  type StatusBody,
  type Subagent,
  type AgentInfo,
} from "./protocol";

/** An event as the timeline holds it: its seq and time, and its body. */
export interface SeqEvent {
  seq: number;
  ts: string;
  body: EventBody;
}

export type TimelineItem =
  | { type: "thought"; ev: SeqEvent }
  | { type: "tool"; callId: string; call?: SeqEvent; result?: SeqEvent }
  | { type: "permission"; pending?: SeqEvent; resolved?: SeqEvent }
  | { type: "answer"; ev: SeqEvent }
  | { type: "note"; ev: SeqEvent }
  | { type: "inject"; ev: SeqEvent }
  | { type: "commit"; ev: SeqEvent };

/** The subagent an item is from, or undefined for the session's own (RFC-044). */
export function subagentOf(item: TimelineItem): Subagent | undefined {
  switch (item.type) {
    case "tool":
      return item.call?.body.subagent ?? item.result?.body.subagent;
    case "permission":
      return item.pending?.body.subagent ?? item.resolved?.body.subagent;
    default:
      return item.ev.body.subagent;
  }
}

export interface Turn {
  turn: number;
  begin?: SeqEvent;
  end?: SeqEvent;
  items: TimelineItem[];
  /** Every seq in the turn, for scrolling to an alert's events. */
  seqs: number[];
}

/**
 * Group events by turn, in order of seq. A tool.call and its tool.result
 * pair by call_id (whichever arrives first holds the slot); a
 * permission.resolved closes the pending one with its call_id, else the
 * oldest still open in its turn.
 */
export function buildTimeline(events: readonly SeqEvent[]): Turn[] {
  const sorted = [...events].sort((a, b) => a.seq - b.seq);
  const turns = new Map<number, Turn>();
  const tools = new Map<string, Extract<TimelineItem, { type: "tool" }>>();
  const perms = new Map<string, Extract<TimelineItem, { type: "permission" }>>();
  const order: Turn[] = [];

  const turnOf = (n: number): Turn => {
    let t = turns.get(n);
    if (!t) {
      t = { turn: n, items: [], seqs: [] };
      turns.set(n, t);
      order.push(t);
    }
    return t;
  };

  for (const ev of sorted) {
    const b = ev.body;
    const t = turnOf(b.turn);
    t.seqs.push(ev.seq);
    switch (b.kind) {
      case "turn.begin":
        t.begin = ev;
        break;
      case "turn.end":
        t.end = ev;
        break;
      case "thought":
        t.items.push({ type: "thought", ev });
        break;
      case "answer":
        t.items.push({ type: "answer", ev });
        break;
      case "note":
        t.items.push({ type: "note", ev });
        break;
      case "inject":
        t.items.push({ type: "inject", ev });
        break;
      case "commit":
        t.items.push({ type: "commit", ev });
        break;
      case "tool.call":
      case "tool.result": {
        const id = b.call_id ?? `seq:${ev.seq}`;
        let item = b.call_id ? tools.get(id) : undefined;
        if (!item) {
          item = { type: "tool", callId: id };
          tools.set(id, item);
          t.items.push(item);
        }
        if (b.kind === "tool.call") item.call = ev;
        else item.result = ev;
        break;
      }
      case "permission.pending": {
        const item: Extract<TimelineItem, { type: "permission" }> = { type: "permission", pending: ev };
        perms.set(b.call_id ?? `seq:${ev.seq}`, item);
        t.items.push(item);
        break;
      }
      case "permission.resolved": {
        let item = b.call_id ? perms.get(b.call_id) : undefined;
        if (!item || item.resolved) {
          const open = t.items.filter(
            (i): i is Extract<TimelineItem, { type: "permission" }> =>
              i.type === "permission" && !i.resolved && i.pending !== undefined,
          );
          // Without a call_id, one that has none either; else the oldest open.
          item = (b.call_id ? undefined : open.find((i) => !i.pending?.body.call_id)) ?? open[0];
        }
        if (item) item.resolved = ev;
        else t.items.push({ type: "permission", resolved: ev });
        break;
      }
    }
  }
  return order.sort((a, b) => a.turn - b.turn);
}

// ---- the session's state ---------------------------------------------------

export interface NoteItem {
  key: string;
  author: Actor;
  text: string;
  ref?: number;
  ts: string;
  /** Set once the sidecar has recorded it as an event. */
  seq?: number;
}

export interface InjectionItem {
  id: string;
  text: string;
  by: Actor;
  ts: string;
  outcome?: InjectOutcome;
  reason?: string;
}

export interface SessionState {
  /** An agent share's card, from its share.open (RFC-043 model 20). */
  agent?: AgentInfo;
  events: SeqEvent[];
  status?: StatusBody;
  alerts: { seq?: number; ts: string; body: AlertBody }[];
  notes: NoteItem[];
  /** Injections this browser requested, and their outcomes. */
  injections: InjectionItem[];
  participants: Participant[];
  /** The coach holding a terminal share's keyboard (RFC-043 model 21). */
  keyboard?: string;
  closed?: string;
  /** the owner kicked this viewer: its connection is closing, and not to be reopened */
  removed?: boolean;
  errors: { code: string; message: string; ref?: string }[];
  policy?: Policy;
  /** What the session can carry, from its share.open (RFC-044). */
  caps?: Caps;
}

export const emptySession: SessionState = {
  events: [],
  alerts: [],
  notes: [],
  injections: [],
  participants: [],
  errors: [],
};

const NoteMergeMs = 2 * 60 * 1000;

/**
 * Add a note, merging it with the same author's same text seen within two
 * minutes: a coach's note arrives both as a relayed `note` frame and as the
 * sidecar's `note` event, and the sender also shows its own at once.
 */
export function addNote(notes: NoteItem[], n: NoteItem): NoteItem[] {
  const at = Date.parse(n.ts);
  const i = notes.findIndex(
    (x) =>
      x.text === n.text &&
      (x.author.user ?? x.author.kind) === (n.author.user ?? n.author.kind) &&
      (x.seq === undefined || n.seq === undefined) &&
      Math.abs(Date.parse(x.ts) - at) < NoteMergeMs,
  );
  if (i < 0) return [...notes, n];
  const merged = [...notes];
  const old = merged[i]!;
  merged[i] = { ...old, seq: old.seq ?? n.seq, ref: old.ref ?? n.ref };
  return merged;
}

/** The session with a note this browser just sent shown at once, before the relay echoes it (addNote merges the two). */
export function withSentNote(s: SessionState, author: Actor, text: string): SessionState {
  const ts = new Date().toISOString();
  return { ...s, notes: addNote(s.notes, { key: `me:${ts}`, author, text, ts }) };
}

/** Who a share's people are, by user id, for the timeline and the notes: its owner, everyone in it now, and me. */
export function namesOf(
  owner: { id: string; name: string },
  participants: readonly Participant[],
  me: { id: string; name: string; email: string },
): ReadonlyMap<string, string> {
  const m = new Map<string, string>([[owner.id, owner.name]]);
  for (const p of participants) m.set(p.user, p.name);
  m.set(me.id, me.name || me.email);
  return m;
}

/** Fold one frame into the session's state. */
export function applyFrame(s: SessionState, e: Envelope): SessionState {
  switch (e.type) {
    case "event": {
      if (e.seq === undefined || s.events.some((x) => x.seq === e.seq)) return s;
      const ev: SeqEvent = { seq: e.seq, ts: e.ts, body: e.body };
      const last = s.events[s.events.length - 1];
      const events = !last || last.seq < ev.seq ? [...s.events, ev] : [...s.events, ev].sort((a, b) => a.seq - b.seq);
      let notes = s.notes;
      if (e.body.kind === "note" && e.body.text) {
        notes = addNote(notes, { key: `seq:${e.seq}`, author: e.body.by, text: e.body.text, ts: e.ts, seq: e.seq });
      }
      return { ...s, events, notes };
    }
    case "status":
      return { ...s, status: e.body };
    case "alert":
      return { ...s, alerts: [...s.alerts, { seq: e.seq, ts: e.ts, body: e.body }] };
    case "note":
      return {
        ...s,
        notes: addNote(s.notes, {
          key: `note:${e.ts}:${e.from.user ?? ""}`,
          author: e.from,
          text: e.body.text,
          ref: e.body.ref,
          ts: e.ts,
        }),
      };
    case "inject.result":
      return {
        ...s,
        injections: s.injections.map((i) =>
          i.id === e.body.id ? { ...i, outcome: e.body.outcome, reason: e.body.reason } : i,
        ),
      };
    case "presence":
      return { ...s, participants: e.body.participants ?? [], keyboard: e.body.keyboard };
    case "share.close":
      return { ...s, closed: e.body.reason || "closed" };
    case "share.open":
      return { ...s, policy: e.body.policy, caps: capsOf(e.body.source, e.body.caps), agent: e.body.agent ?? s.agent };
    case "error":
      return {
        ...s,
        ...(e.body.code === "removed" ? { removed: true } : {}),
        errors: [...s.errors, e.body],
        // A refused injection request comes back as an error naming its id.
        injections: s.injections.map((i) =>
          e.body.ref && i.id === e.body.ref && !i.outcome
            ? { ...i, outcome: "denied", reason: e.body.message }
            : i,
        ),
      };
    default:
      return s;
  }
}

/** Whether this viewer may request an injected turn (RFC-043 "Roles"). */
export function canInject(role: string, caps: Caps, policy: Policy | undefined): boolean {
  return role === "coach" && caps.inject === true && policy?.inject === true;
}

/**
 * Whether this viewer may write notes: the owner always, a coach when the
 * policy allows notes, a spectator when it lets everyone chat too (RFC-043
 * model 21).
 */
export function canNote(role: string, policy: Policy | undefined): boolean {
  if (role === "owner") return true;
  if (policy?.notes === false) return false;
  return role === "coach" || (role === "spectator" && policy?.chat === true);
}
